// components/common/MapComponent.tsx
import {
  AdvancedMarker,
  APIProvider,
  Map,
  Pin,
  useMap
} from '@vis.gl/react-google-maps';
import MapCameraController from 'components/common/MapCameraController'; // ✅ ADICIONAR IMPORT
import MapLegend from 'components/common/MapLegend';
import MapDisplacementLine from 'components/video/MapDisplacementLine';
import { getServiceColor } from 'constants/serviceColors';
import React, { useEffect, useMemo, useState } from 'react';
import { DisplacementResult } from 'utils/geoCalculations';
import { getServiceImageStyle, XOverlay } from 'utils/signImageFilters';

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  color?: string;
  icon?: string;
  type?: 'current' | 'sign' | 'image';
  imageUrl?: string;
  isSelected?: boolean;
  symbolName?: string;
  isOverlay?: boolean;
  projectName?: string;
  distance?: number;
  serviceId?: number;
}

export type TrailDisplayMode = 'all' | 'window' | 'simplified' | 'none';

interface MapComponentProps {
  lat?: number;
  lng?: number;
  zoom?: number;
  title?: string;
  markers?: MapMarker[];
  imageTrail?: Array<{
    lat: number;
    lng: number;
    id: number;
    filename: string;
  }>;
  currentImageIndex?: number;
  onMarkerClick?: (markerId: string) => void;
  onMapClick?: (lat: number, lng: number) => void;
  mapClickEnabled?: boolean;
  mapDragModeEnabled?: boolean;
  draggingMapSignId?: number | null;
  onMarkerDragStart?: (signId: number, lat: number, lng: number) => void;
  onMarkerDragMove?: (lat: number, lng: number) => void;
  onMarkerDragEnd?: (lat: number, lng: number) => void;
  height?: string;
  showZoomControls?: boolean;
  displacementResult?: DisplacementResult | null;
  showDisplacementLine?: boolean;
  cameraBearing?: number | null;
  showTrailPoints?: boolean;
  onToggleTrailPoints?: () => void;
  showSignImages?: boolean;
  onToggleSignImages?: () => void;
  trailDisplayMode?: TrailDisplayMode;
  onTrailDisplayModeChange?: (mode: TrailDisplayMode) => void;
  selectedSignMarkerId?: string | null;
}

const ImageTrailLine: React.FC<{
  trail: Array<{ lat: number; lng: number; id: number; filename: string }>;
}> = ({ trail }) => {
  const map = useMap();
  const [polyline, setPolyline] = useState<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map || !trail || trail.length < 2) {
      if (polyline) {
        polyline.setMap(null);
        setPolyline(null);
      }
      return;
    }

    const path = trail
      .filter(point => point.lat !== null && point.lng !== null)
      .map(point => ({ lat: point.lat, lng: point.lng }));

    if (path.length < 2) return;

    if (!polyline) {
      const newPolyline = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#0f62a7ff',
        strokeOpacity: 0.5,
        strokeWeight: 6,
        map: map
      });
      setPolyline(newPolyline);
    } else {
      polyline.setPath(path);
    }

    return () => {
      if (polyline) {
        polyline.setMap(null);
      }
    };
  }, [map, trail]);

  return null;
};

const MapMarkers: React.FC<{
  markers: MapMarker[];
  onMarkerClick?: (markerId: string) => void;
  mapDragModeEnabled?: boolean;
  draggingMapSignId?: number | null;
  onMarkerDragStart?: (signId: number, lat: number, lng: number) => void;
  onMarkerDragMove?: (lat: number, lng: number) => void;
  onMarkerDragEnd?: (lat: number, lng: number) => void;
  showSignImages?: boolean;
}> = ({
  markers,
  onMarkerClick,
  mapDragModeEnabled,
  draggingMapSignId,
  onMarkerDragStart,
  onMarkerDragMove,
  onMarkerDragEnd,
  showSignImages = false
}) => {
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickedMarkerId, setLastClickedMarkerId] = useState<string>('');
  const DOUBLE_CLICK_DELAY = 300;

  const handleMarkerClick = (markerId: string, isSignMarker = false) => {
    const currentTime = Date.now();

    if (
      isSignMarker &&
      lastClickedMarkerId === markerId &&
      currentTime - lastClickTime < DOUBLE_CLICK_DELAY
    ) {
      if (markerId.startsWith('sign_') || markerId.startsWith('overlay_')) {
        onMarkerClick?.(`edit_${markerId}`);
      }
    } else {
      onMarkerClick?.(markerId);
    }

    setLastClickTime(currentTime);
    setLastClickedMarkerId(markerId);
  };

  return (
    <>
      {markers.map(marker => {
        if (marker.type === 'current') {
          return (
            <AdvancedMarker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => handleMarkerClick(marker.id, true)}
              title={marker.title}
              zIndex={100}
            >
              <div style={{ position: 'relative' }}>
                <Pin
                  background="#dc3545"
                  borderColor="#fff"
                  glyphColor="#fff"
                  scale={1.2}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(220, 53, 69, 0.3)',
                    animation: 'pulse 2s infinite'
                  }}
                />
              </div>
            </AdvancedMarker>
          );
        }

        if (marker.type === 'sign') {
          const signId = parseInt(
            marker.id.replace('sign_', '').replace('overlay_', '')
          );
          const isDragging = draggingMapSignId === signId;
          const isOverlay = marker.isOverlay || false;
          const imageStyle = getServiceImageStyle(marker.serviceId, isOverlay);
          const baseSize = marker.isSelected ? 52 : 40;
          const imageSize = marker.isSelected ? 48 : 36;

          const signImagePath = marker.symbolName
            ? `/images/signs/${marker.symbolName}`
            : null;

          return (
            <AdvancedMarker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => {
                if (!mapDragModeEnabled) {
                  handleMarkerClick(marker.id, true);
                }
              }}
              onDragStart={e => {
                if (mapDragModeEnabled && !marker.isOverlay && e.latLng) {
                  onMarkerDragStart?.(signId, e.latLng.lat(), e.latLng.lng());
                }
              }}
              onDrag={e => {
                if (mapDragModeEnabled && !marker.isOverlay && e.latLng) {
                  onMarkerDragMove?.(e.latLng.lat(), e.latLng.lng());
                }
              }}
              onDragEnd={e => {
                if (mapDragModeEnabled && !marker.isOverlay && e.latLng) {
                  onMarkerDragEnd?.(e.latLng.lat(), e.latLng.lng());
                }
              }}
              draggable={mapDragModeEnabled && !marker.isOverlay}
              title={marker.title}
              zIndex={
                isDragging
                  ? 400
                  : marker.isSelected
                  ? 300
                  : marker.isOverlay
                  ? 100
                  : 200
              }
            >
              {showSignImages && signImagePath ? (
                // 🖼️ MODO IMAGEM - Círculo branco com imagem da placa
                <div
                  style={{
                    position: 'relative',
                    width: `${baseSize}px`,
                    height: `${baseSize}px`,
                    cursor: marker.isOverlay
                      ? 'pointer'
                      : mapDragModeEnabled
                      ? 'move'
                      : 'pointer',
                    transition: isDragging ? 'none' : 'all 0.2s ease',
                    // ✅ PULSE quando selecionado
                    animation: marker.isSelected
                      ? 'pulse-sign-strong 0.8s ease-in-out infinite'
                      : 'none'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: imageStyle.borderColor
                        ? `${imageStyle.borderWidth || '3px'} solid ${
                            imageStyle.borderColor
                          }`
                        : marker.isSelected
                        ? '4px solid #0d6efd'
                        : '3px solid white',
                      boxShadow: marker.isSelected
                        ? '0 0 0 4px rgba(13, 110, 253, 0.3), 0 4px 12px rgba(0,0,0,0.4)'
                        : '0 2px 6px rgba(0,0,0,0.3)',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <img
                      src={signImagePath}
                      alt={marker.title}
                      style={{
                        width: `${imageSize}px`,
                        height: `${imageSize}px`,
                        objectFit: 'contain',
                        filter: imageStyle.filter,
                        opacity: imageStyle.opacity || 1
                      }}
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  {imageStyle.showXOverlay && (
                    <XOverlay size={baseSize * 0.8} />
                  )}

                  {/* ✅ Badge com número/ícone */}
                  {marker.icon && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-6px',
                        right: '-6px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: marker.isOverlay
                          ? '#6c757d'
                          : getServiceColor(marker.serviceId).rgbaColor,
                        border: marker.isSelected
                          ? '2px solid #0d6efd'
                          : '2px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: marker.isSelected
                          ? '0 0 0 3px rgba(13, 110, 253, 0.3), 0 1px 3px rgba(0,0,0,0.4)'
                          : '0 1px 3px rgba(0,0,0,0.4)',
                        transition: 'all 0.3s ease',
                        opacity: marker.isOverlay ? 0.8 : 1,
                        pointerEvents: 'none'
                      }}
                    >
                      <span
                        style={{
                          color: 'white',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          fontFamily: 'monospace',
                          userSelect: 'none'
                        }}
                      >
                        {marker.icon}
                      </span>
                    </div>
                  )}

                  {marker.isOverlay && marker.distance !== undefined && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(108, 117, 125, 0.9)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        pointerEvents: 'none'
                      }}
                    >
                      {marker.distance.toFixed(1)}m
                    </div>
                  )}
                </div>
              ) : (
                // 🔢 MODO CÍRCULO NUMERADO - Círculo colorido com número
                <div
                  style={{
                    position: 'relative',
                    width: marker.isSelected ? '32px' : '28px',
                    height: marker.isSelected ? '32px' : '28px',
                    cursor: marker.isOverlay
                      ? 'pointer'
                      : mapDragModeEnabled
                      ? 'move'
                      : 'pointer',
                    transition: isDragging ? 'none' : 'all 0.2s ease'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      backgroundColor: marker.isOverlay
                        ? 'rgba(108, 117, 125, 0.6)'
                        : getServiceColor(marker.serviceId).rgbaColor,
                      border: marker.isSelected
                        ? '3px solid #000'
                        : '2px solid #fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: marker.isSelected
                        ? '0 0 0 3px rgba(13, 110, 253, 0.3), 0 2px 6px rgba(0,0,0,0.4)'
                        : '0 2px 4px rgba(0,0,0,0.3)',
                      transition: 'all 0.3s ease',
                      animation: marker.isSelected
                        ? 'pulse-sign-strong 0.8s ease-in-out infinite'
                        : 'none',
                      filter: marker.isSelected
                        ? 'drop-shadow(0 0 4px rgba(13, 110, 253, 0.6))'
                        : 'none'
                    }}
                  >
                    <span
                      style={{
                        color: 'white',
                        fontSize: marker.isSelected ? '14px' : '12px',
                        fontWeight: 'bold',
                        fontFamily: 'Arial, sans-serif',
                        userSelect: 'none',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {marker.icon}
                    </span>
                  </div>

                  {marker.isOverlay && marker.distance !== undefined && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(108, 117, 125, 0.9)',
                        color: 'white',
                        padding: '2px 5px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        pointerEvents: 'none'
                      }}
                    >
                      {marker.distance.toFixed(1)}m
                    </div>
                  )}
                </div>
              )}
            </AdvancedMarker>
          );
        }

        if (marker.type === 'image') {
          return (
            <AdvancedMarker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => handleMarkerClick(marker.id)}
              title={marker.title}
              zIndex={50}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: '#4299e1',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              />
            </AdvancedMarker>
          );
        }

        return null;
      })}
    </>
  );
};

const MapComponent: React.FC<MapComponentProps> = ({
  lat = -27.5969,
  lng = -48.5495,
  zoom = 13,
  markers = [],
  imageTrail = [],
  currentImageIndex,
  onMarkerClick,
  onMapClick,
  mapClickEnabled = false,
  mapDragModeEnabled = false,
  draggingMapSignId = null,
  onMarkerDragStart,
  onMarkerDragMove,
  onMarkerDragEnd,
  height = '600px',
  showZoomControls = true,
  displacementResult = null,
  showDisplacementLine = false,
  cameraBearing = null,
  showTrailPoints = true,
  onToggleTrailPoints,
  showSignImages = false,
  onToggleSignImages,
  trailDisplayMode = 'window',
  selectedSignMarkerId = null
}) => {
  const [mouseLatLng, setMouseLatLng] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // ✅ NOVO: Encontrar o marker da imagem atual
  const currentImageMarker = useMemo(() => {
    const currentMarker = markers.find(m => m.type === 'current');

    if (currentMarker) {
      // ✅ IMPORTANTE: Criar um ID único que muda com cada imagem
      return {
        ...currentMarker,
        id: `current_${currentImageIndex}` // Força mudança de ID a cada frame
      };
    }

    return null;
  }, [markers, currentImageIndex]); // ✅ Adicionar currentImageIndex como dependência

  useEffect(() => {
    if (currentImageMarker) {
      console.log('📍 [MapComponent] currentImageMarker atualizado:', {
        id: currentImageMarker.id,
        lat: currentImageMarker.lat,
        lng: currentImageMarker.lng,
        frame: currentImageIndex
      });
    }
  }, [currentImageMarker, currentImageIndex]);

  const optimizedMarkers = useMemo(() => {
    if (!showTrailPoints || trailDisplayMode === 'none') {
      return {
        markers: markers,
        stats: { markersCount: 0, simplificationRatio: 0 }
      };
    }

    let imageMarkers: MapMarker[] = [];
    const WINDOW_SIZE = 50;

    if (trailDisplayMode === 'window' && currentImageIndex !== undefined) {
      const windowStart = Math.max(0, currentImageIndex - WINDOW_SIZE);
      const windowEnd = Math.min(
        imageTrail.length,
        currentImageIndex + WINDOW_SIZE
      );

      imageMarkers = imageTrail
        .slice(windowStart, windowEnd)
        .map((point, idx) => ({
          id: `trail_${point.id}`, // ✅ CORRIGIR: usar trail_ + ID da imagem
          lat: point.lat,
          lng: point.lng,
          title: `Frame ${windowStart + idx + 1}`,
          type: 'image' as const,
          isSelected: windowStart + idx === currentImageIndex
        }));
    } else if (trailDisplayMode === 'simplified') {
      const SIMPLIFICATION_FACTOR = 10;
      imageMarkers = imageTrail
        .filter((_, idx) => idx % SIMPLIFICATION_FACTOR === 0)
        .map((point, idx) => ({
          id: `trail_${point.id}`, // ✅ CORRIGIR: usar trail_ + ID da imagem
          lat: point.lat,
          lng: point.lng,
          title: `Frame ${idx * SIMPLIFICATION_FACTOR + 1}`,
          type: 'image' as const,
          isSelected: idx * SIMPLIFICATION_FACTOR === currentImageIndex
        }));
    } else if (trailDisplayMode === 'all') {
      imageMarkers = imageTrail.map((point, idx) => ({
        id: `trail_${point.id}`, // ✅ CORRIGIR: usar trail_ + ID da imagem
        lat: point.lat,
        lng: point.lng,
        title: `Frame ${idx + 1}`,
        type: 'image' as const,
        isSelected: idx === currentImageIndex
      }));
    }

    return {
      markers: [...markers, ...imageMarkers],
      stats: {
        markersCount: imageMarkers.length,
        simplificationRatio:
          imageTrail.length > 0
            ? ((imageMarkers.length / imageTrail.length) * 100).toFixed(1)
            : 0
      }
    };
  }, [
    markers,
    imageTrail,
    currentImageIndex,
    showTrailPoints,
    trailDisplayMode
  ]);

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
      <div style={{ position: 'relative', width: '100%', height }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Map
            defaultCenter={{ lat, lng }}
            defaultZoom={zoom}
            disableDefaultUI={!showZoomControls}
            zoomControl={showZoomControls}
            mapTypeControl={true}
            streetViewControl={true}
            fullscreenControl={true}
            gestureHandling="greedy"
            onClick={(e: any) => {
              if (mapClickEnabled && e.detail?.latLng && onMapClick) {
                onMapClick(e.detail.latLng.lat, e.detail.latLng.lng);
              }
            }}
            onMousemove={(e: any) => {
              if (mapClickEnabled && e.detail?.latLng) {
                setMouseLatLng({
                  lat: e.detail.latLng.lat,
                  lng: e.detail.latLng.lng
                });
              }
            }}
            mapId="main-map"
          >
            {showTrailPoints && imageTrail.length > 1 && (
              <ImageTrailLine trail={imageTrail} />
            )}

            <MapCameraController
              markers={optimizedMarkers.markers}
              selectedSignMarkerId={selectedSignMarkerId}
              currentImageMarker={currentImageMarker}
              followCurrentPosition={true}
            />

            <MapMarkers
              markers={optimizedMarkers.markers}
              onMarkerClick={onMarkerClick}
              mapDragModeEnabled={mapDragModeEnabled}
              draggingMapSignId={draggingMapSignId}
              onMarkerDragStart={onMarkerDragStart}
              onMarkerDragMove={onMarkerDragMove}
              onMarkerDragEnd={onMarkerDragEnd}
              showSignImages={showSignImages}
            />

            <MapDisplacementLine
              displacementResult={displacementResult}
              show={showDisplacementLine}
              cameraBearing={cameraBearing}
            />
          </Map>
        </div>

        {mapClickEnabled && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: 'rgba(40, 167, 69, 0.95)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              animation: 'pulse 2s infinite',
              cursor: 'crosshair',
              minWidth: '200px',
              pointerEvents: 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>📍</span>
              <span>Clique no mapa para adicionar placa</span>
            </div>
            {mouseLatLng && (
              <div
                style={{
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  padding: '4px 6px',
                  borderRadius: '4px'
                }}
              >
                Lat: {mouseLatLng.lat.toFixed(6)}
                <br />
                Lng: {mouseLatLng.lng.toFixed(6)}
              </div>
            )}
          </div>
        )}

        <MapLegend
          defaultCollapsed={true}
          showSignImages={showSignImages}
          onToggleSignImages={onToggleSignImages}
          showTrailPoints={showTrailPoints}
          onToggleTrailPoints={onToggleTrailPoints}
          trailMarkersCount={optimizedMarkers.stats?.markersCount || 0}
          signMarkersCount={
            markers.filter(m => m.type === 'sign' && !m.isOverlay).length
          }
          overlayMarkersCount={
            markers.filter(m => m.type === 'sign' && m.isOverlay).length
          }
        />
      </div>
    </APIProvider>
  );
};

const styles = document.createElement('style');
styles.textContent = `
  @keyframes pulse-sign-strong {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.7);
    }
    50% {
      transform: scale(1.2);
      box-shadow: 0 0 0 15px rgba(13, 110, 253, 0);
    }
  }
`;
if (!document.head.querySelector('#pulse-sign-strong-styles')) {
  styles.id = 'pulse-sign-strong-styles';
  document.head.appendChild(styles);
}

export default MapComponent;
